import { useEffect, useMemo, useRef, useState } from 'react';

import type { Annotation, DrawStroke, RevealStep, Slide, Spotlight } from '../schema';
import {
  selectStepAnnotations,
  selectStepReveals,
  selectVisibleSpotlights,
  selectVisibleStrokes,
  stepHasTiming,
} from './reveal-timing';
import { CUE_ANNOTATION, CUE_SPOTLIGHT, CUE_STROKE, cueForElement, playSfx } from '../sfx';

/** The per-frame visual state a host renders into `SlideStage`. */
export interface TimedFrame {
  /**
   * Verbatim set — when `timingActive` an EMPTY set means "nothing yet", not
   * "show all"; hosts keep their own empty→null convention for untimed steps.
   */
  revealedElementIds: Set<string>;
  annotations: Annotation[];
  drawStrokes: DrawStroke[];
  spotlights: Spotlight[];
  /** True while the current step's intra-step ticker is driving the frame. */
  timingActive: boolean;
}

/**
 * Word-timed playback frame for the current slide/step. While the current step
 * carries intra-step timing (element timings, timed annotations, strokes,
 * spotlights), a rAF ticker gates its visuals by the narration clock — a
 * wall-clock from when the step began — so they appear/disappear exactly as the
 * teach-studio timeline authored. Elements/annotations from prior steps stay
 * (the engine's cumulative sets are the base); strokes/spotlights accumulate
 * via the cumulative selectors (prior steps at ∞ — endMs'd ones are gone).
 *
 * Extracted from `SlidePlayer` so hosts that drive `SlideStage` directly (the
 * Electron classroom) share the exact same timing behaviour.
 */
export function useTimedFrame({
  slide,
  currentStep,
  currentStepIndex,
  baseRevealed,
  baseAnnotations,
  fireSfx = false,
}: {
  slide: Slide | null;
  currentStep: RevealStep | null;
  currentStepIndex: number;
  /** Engine's cumulative revealed-ids set (`player.revealedElementIds`). */
  baseRevealed: Set<string>;
  /** Engine's cumulative annotations (`player.accumulatedAnnotations`). */
  baseAnnotations: Annotation[];
  /** Fire reveal sound cues (element/annotation/stroke/spotlight). */
  fireSfx?: boolean;
}): TimedFrame {
  const steps = useMemo(() => slide?.revealSteps ?? [], [slide]);

  // Wall-clock origin of the current step (same basis the camera ticker uses).
  const stepStartRef = useRef(0);
  useEffect(() => {
    stepStartRef.current = typeof performance !== 'undefined' ? performance.now() : 0;
  }, [currentStep?.id]);

  const [timed, setTimed] = useState<{
    ids: Set<string>;
    annos: Annotation[];
    strokes: DrawStroke[];
    spots: Spotlight[];
  } | null>(null);

  useEffect(() => {
    if (!currentStep || !stepHasTiming(currentStep)) {
      setTimed(null);
      return undefined;
    }
    let raf = 0;
    let sig = '';
    const tick = () => {
      const now =
        (typeof performance !== 'undefined' ? performance.now() : 0) - stepStartRef.current;
      // Elements: engine's cumulative set minus this step's ids, plus the timed
      // subset visible right now.
      const ids = new Set(baseRevealed);
      for (const id of currentStep.elementIds ?? []) ids.delete(id);
      for (const id of selectStepReveals(currentStep, now)) ids.add(id);
      // Annotations: same swap-in for the current step.
      const stepAnnoIds = new Set((currentStep.annotations ?? []).map((a) => a.id));
      const annos = baseAnnotations
        .filter((a) => !stepAnnoIds.has(a.id))
        .concat(selectStepAnnotations(currentStep, now));
      // Strokes/spotlights: cumulative across played steps (prior at ∞).
      const strokes = selectVisibleStrokes(steps, currentStepIndex, now);
      const spots = selectVisibleSpotlights(steps, currentStepIndex, now);
      const nextSig =
        [...ids].sort().join(',') +
        '|' +
        annos.map((a) => a.id).join(',') +
        '|' +
        strokes.map((s) => s.id).join(',') +
        '|' +
        spots.map((s) => s.id).join(',');
      if (nextSig !== sig) {
        sig = nextSig;
        setTimed({ ids, annos, strokes, spots });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [currentStep, currentStepIndex, steps, baseRevealed, baseAnnotations]);

  // Static frame (step has no intra-step timing): the engine's cumulative sets,
  // plus strokes/spotlights of played-so-far steps evaluated at ∞ (persistent
  // ones stay; ended ones are gone).
  const frame = useMemo<TimedFrame>(() => {
    if (timed) {
      return {
        revealedElementIds: timed.ids,
        annotations: timed.annos,
        drawStrokes: timed.strokes,
        spotlights: timed.spots,
        timingActive: true,
      };
    }
    return {
      revealedElementIds: baseRevealed,
      annotations: baseAnnotations,
      drawStrokes: selectVisibleStrokes(steps, currentStepIndex, Number.POSITIVE_INFINITY),
      spotlights: selectVisibleSpotlights(steps, currentStepIndex, Number.POSITIVE_INFINITY),
      timingActive: false,
    };
  }, [timed, baseRevealed, baseAnnotations, steps, currentStepIndex]);

  // ── Reveal sound cues ──────────────────────────────────────────────────────
  // Fire once per newly-visible element/annotation/stroke/spotlight. Seed the
  // prev-sets on slide change so seeks/instant-reveals don't machine-gun cues.
  const typeMap = useMemo(() => {
    const m = new Map<string, string>();
    slide?.rows?.forEach((r) =>
      r.columns?.forEach((c) => c.elements?.forEach((e) => m.set(e.id, e.type))),
    );
    return m;
  }, [slide]);
  const prevRevealed = useRef<Set<string>>(new Set());
  const prevAnno = useRef<Set<string>>(new Set());
  const prevStroke = useRef<Set<string>>(new Set());
  const prevSpot = useRef<Set<string>>(new Set());
  const seededSlideRef = useRef<string | null>(null);

  useEffect(() => {
    const annoIds = new Set(frame.annotations.map((a) => a.id));
    const strokeIds = new Set(frame.drawStrokes.map((s) => s.id));
    const spotIds = new Set(frame.spotlights.map((s) => s.id));
    const seeding = seededSlideRef.current !== (slide?.id ?? null);
    if (!seeding && fireSfx) {
      for (const id of frame.revealedElementIds)
        if (!prevRevealed.current.has(id)) playSfx(cueForElement(typeMap.get(id) ?? 'text'));
      for (const id of annoIds) if (!prevAnno.current.has(id)) playSfx(CUE_ANNOTATION);
      for (const id of strokeIds) if (!prevStroke.current.has(id)) playSfx(CUE_STROKE);
      for (const id of spotIds) if (!prevSpot.current.has(id)) playSfx(CUE_SPOTLIGHT);
    }
    seededSlideRef.current = slide?.id ?? null;
    prevRevealed.current = new Set(frame.revealedElementIds);
    prevAnno.current = annoIds;
    prevStroke.current = strokeIds;
    prevSpot.current = spotIds;
  }, [frame, fireSfx, typeMap, slide?.id]);

  return frame;
}
